import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link as MuiLink,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { AttachFile, CheckCircleOutline, Save } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import dayjs from 'dayjs'
import { PageHeader } from '@/components/common/PageHeader'
import { onboardingService } from '@/services/onboardingService'
import { documentoService } from '@/services/documentoService'
import { ConteudoOnboardingAdmin, EtapaOnboarding } from '@/types/onboarding'

/**
 * Conteúdo institucional do primeiro acesso (PDF do Consultor, Etapa 4) — edição pelo ADMIN.
 *
 * **Por que esta tela existe.** O wizard e o endpoint de edição foram entregues juntos, mas
 * **nenhuma tela chamava o endpoint**: na prática, não havia como inserir o material sem
 * chamar a API na mão. O time comercial pediu exatamente isto, duas vezes e nas mesmas
 * palavras (04/08): sobre o vídeo, *"importante existir o ambiente onde será inserido"*;
 * sobre o texto, *"é importante que exista o local para incluir o arquivo"*.
 *
 * Daí as três formas conviverem por etapa — **texto**, **link de vídeo** e **arquivo
 * anexo**. Quem escreve material institucional costuma entregar um PDF, não digitar numa
 * caixa de texto.
 */

/**
 * Os sete tópicos que o PDF lista como obrigatórios na apresentação da Originei.
 *
 * <p>Ficam como **guia**, não como sete campos. Estruturá-los em campos separados
 * obrigaria o time à minha divisão do texto; listá-los aqui lembra o que não pode faltar e
 * deixa a redação com quem escreve.
 */
const TOPICOS_OBRIGATORIOS = [
  'Modelo de negócios',
  'Papel do consultor',
  'Papel da Originei',
  'Papel dos parceiros',
  'Como funciona a reserva',
  'Como funciona o pagamento de comissões',
  'Responsabilidades do consultor',
]

const TIPO_ENTIDADE = 'ONBOARDING'

const ConteudoInstitucionalPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [conteudos, setConteudos] = useState<ConteudoOnboardingAdmin[]>([])
  const [rascunhos, setRascunhos] = useState<Record<string, ConteudoOnboardingAdmin>>({})
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState<EtapaOnboarding | null>(null)
  const [enviando, setEnviando] = useState<EtapaOnboarding | null>(null)
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const lista = await onboardingService.listarConteudos()
      setConteudos(lista)
      setRascunhos(Object.fromEntries(lista.map((c) => [c.etapa, { ...c }])))
    } catch {
      setErro('Não foi possível carregar o conteúdo do primeiro acesso.')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const alterar = (etapa: EtapaOnboarding, campo: keyof ConteudoOnboardingAdmin, valor: unknown) => {
    setRascunhos((prev) => ({ ...prev, [etapa]: { ...prev[etapa], [campo]: valor } }))
  }

  const salvar = async (etapa: EtapaOnboarding) => {
    const rascunho = rascunhos[etapa]
    if (!rascunho) return
    setSalvando(etapa)
    try {
      await onboardingService.salvarConteudo(etapa, {
        titulo: rascunho.titulo,
        conteudo: rascunho.conteudo ?? null,
        videoUrl: rascunho.videoUrl ?? null,
        documentoId: rascunho.documentoId ?? null,
        ativa: rascunho.ativa,
      })
      enqueueSnackbar('Conteúdo salvo. Vale no próximo acesso do consultor.', {
        variant: 'success',
      })
      await carregar()
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Falha ao salvar.', { variant: 'error' })
    } finally {
      setSalvando(null)
    }
  }

  const anexar = async (etapa: EtapaOnboarding, arquivo: File) => {
    setEnviando(etapa)
    try {
      // O arquivo vai para o módulo de documentos; aqui guardamos só a referência. Salvar
      // logo em seguida evita o anexo ficar órfão se o ADMIN fechar a tela.
      const enviado = await documentoService.upload(arquivo, 'MANUAL', etapa, TIPO_ENTIDADE)
      const rascunho = rascunhos[etapa]
      await onboardingService.salvarConteudo(etapa, {
        titulo: rascunho.titulo,
        conteudo: rascunho.conteudo ?? null,
        videoUrl: rascunho.videoUrl ?? null,
        documentoId: enviado.id,
        ativa: rascunho.ativa,
      })
      enqueueSnackbar('Arquivo anexado.', { variant: 'success' })
      await carregar()
    } catch {
      enqueueSnackbar('Falha ao anexar o arquivo.', { variant: 'error' })
    } finally {
      setEnviando(null)
    }
  }

  const baixar = async (documentoId: string) => {
    try {
      const blob = await documentoService.baixarArquivo(documentoId)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'material-institucional'
      link.click()
      URL.revokeObjectURL(url)
    } catch {
      enqueueSnackbar('Falha ao baixar o arquivo.', { variant: 'error' })
    }
  }

  if (carregando) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress aria-label="Carregando conteúdo institucional" />
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Conteúdo do primeiro acesso"
        subtitle="Material que o consultor vê antes de poder criar reservas"
      />

      {erro && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button size="small" color="inherit" onClick={() => void carregar()}>
              Tentar de novo
            </Button>
          }
        >
          {erro}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        Cada etapa aceita <strong>texto</strong>, <strong>link de vídeo</strong> e{' '}
        <strong>arquivo anexo</strong> — as três convivem. Etapa desligada some do wizard e
        sai da contagem de conclusão, que é como o tutorial fica de fora até ser produzido.
      </Alert>

      <Stack spacing={3}>
        {conteudos.map((c) => {
          const rascunho = rascunhos[c.etapa] ?? c
          const ehApresentacao = c.etapa === 'APRESENTACAO'
          return (
            <Card key={c.etapa} sx={{ p: 3, opacity: rascunho.ativa ? 1 : 0.6 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {c.titulo}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {c.etapa}
                    {c.updatedAt && ` · atualizado em ${dayjs(c.updatedAt).format('DD/MM/YYYY HH:mm')}`}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={rascunho.ativa}
                      onChange={(e) => alterar(c.etapa, 'ativa', e.target.checked)}
                    />
                  }
                  label={rascunho.ativa ? 'Ativa' : 'Desligada'}
                />
              </Stack>

              {ehApresentacao && (
                <Alert severity="warning" icon={<CheckCircleOutline />} sx={{ mb: 2 }}>
                  <AlertTitle>Tópicos obrigatórios do PDF</AlertTitle>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    A apresentação precisa cobrir estes sete pontos:
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {TOPICOS_OBRIGATORIOS.map((t) => (
                      <Chip key={t} label={t} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Alert>
              )}

              <Stack spacing={2}>
                <TextField
                  label="Título"
                  value={rascunho.titulo}
                  onChange={(e) => alterar(c.etapa, 'titulo', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Texto"
                  value={rascunho.conteudo ?? ''}
                  onChange={(e) => alterar(c.etapa, 'conteudo', e.target.value)}
                  multiline
                  minRows={4}
                  fullWidth
                />
                <TextField
                  label="Link do vídeo"
                  value={rascunho.videoUrl ?? ''}
                  onChange={(e) => alterar(c.etapa, 'videoUrl', e.target.value)}
                  placeholder="https://…"
                  fullWidth
                />

                <Divider />

                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <input
                    type="file"
                    hidden
                    ref={(el) => {
                      inputRefs.current[c.etapa] = el
                    }}
                    onChange={(e) => {
                      const arquivo = e.target.files?.[0]
                      if (arquivo) void anexar(c.etapa, arquivo)
                      e.target.value = ''
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<AttachFile />}
                    onClick={() => inputRefs.current[c.etapa]?.click()}
                    disabled={enviando === c.etapa}
                  >
                    {enviando === c.etapa ? 'Enviando…' : 'Anexar arquivo'}
                  </Button>
                  {c.documentoId ? (
                    <MuiLink
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => void baixar(c.documentoId as string)}
                    >
                      Baixar o arquivo anexado
                    </MuiLink>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Nenhum arquivo anexado.
                    </Typography>
                  )}
                </Stack>

                <Box>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={() => void salvar(c.etapa)}
                    disabled={salvando === c.etapa || !rascunho.titulo.trim()}
                  >
                    {salvando === c.etapa ? 'Salvando…' : 'Salvar etapa'}
                  </Button>
                </Box>
              </Stack>
            </Card>
          )
        })}
      </Stack>
    </Box>
  )
}

export default ConteudoInstitucionalPage
